import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import { useNotification } from "../NotificationContext";
import CreateAlertDialog from "../modals/CreateAlertDialog";
import AlertSourceModal from "../modals/AlertSourceModal";
import api from "@/api";

const AlertsPanel = () => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const { showError, showSuccess } = useNotification();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [loadingAlert, setLoadingAlert] = useState(false);

  // Fetch all active alerts
  useEffect(() => {
    fetchAlerts();
    // Poll every 30 seconds to keep alerts fresh
    const interval = setInterval(fetchAlerts, 30000);

    // Listen for alert creation events
    const handleAlertCreated = () => {
      fetchAlerts();
    };
    window.addEventListener("alertCreated", handleAlertCreated);

    return () => {
      clearInterval(interval);
      window.removeEventListener("alertCreated", handleAlertCreated);
    };
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get("/alerts", {
        params: { is_active: true, include_source: true },
      });
      setAlerts(response.data || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAlert = async (alertId, isActive) => {
    try {
      const response = await api.patch(`/alerts/${alertId}`, {
        is_active: !isActive,
      });
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? response.data : a)),
      );
      showSuccess(isActive ? "Alert disabled" : "Alert enabled");
    } catch (error) {
      console.error("Error toggling alert:", error);
      showError("Failed to update alert");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm("Are you sure you want to delete this alert?")) {
      return;
    }

    try {
      await api.delete(`/alerts/${alertId}`);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      showSuccess("Alert deleted");
    } catch (error) {
      console.error("Error deleting alert:", error);
      showError("Failed to delete alert");
    }
  };

  const handleAlertClick = async (alert) => {
    // Optimistically open with basic data, then enrich with price change
    setSelectedAlert(alert);
    setLoadingAlert(true);
    try {
      const response = await api.get(`/alerts/${alert.id}`);
      setSelectedAlert(response.data);
    } catch (error) {
      console.error("Error fetching alert detail:", error);
    } finally {
      setLoadingAlert(false);
    }
  };

  const handleAlertCreated = (newAlert) => {
    // Dispatch event for other panels and refresh local list
    window.dispatchEvent(new CustomEvent("alertCreated", { detail: newAlert }));
    fetchAlerts();
    setIsAlertModalOpen(false);
  };

  if (loading) {
    return (
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="p-4 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <div className="p-4">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
              Active Alerts
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {alerts.length} {alerts.length === 1 ? "alert" : "alerts"} active
            </p>
          </div>
          <button
            onClick={() => setIsAlertModalOpen(true)}
            className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Create alert"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>

        {/* Alerts List */}
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No active alerts
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Create alerts on ticker pages
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert) => {
              const isAbove = alert.alert_type === "PRICE_ABOVE";
              const isTriggered = alert.triggered_at !== null;

              return (
                <div
                  key={alert.id}
                  className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
                  onClick={() => handleAlertClick(alert)}
                >
                  {/* Header: Ticker and Actions */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {/* Alert Icon */}
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isAbove
                            ? "bg-green-100 dark:bg-green-900/30"
                            : "bg-red-100 dark:bg-red-900/30"
                        }`}
                      >
                        <svg
                          className={`w-4 h-4 ${
                            isAbove
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={
                              isAbove
                                ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                : "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                            }
                          />
                        </svg>
                      </div>

                      {/* Ticker */}
                      <span className="font-bold text-gray-900 dark:text-white">
                        {alert.ticker}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Toggle Active */}
                      <button
                        onClick={() =>
                          handleToggleAlert(alert.id, alert.is_active)
                        }
                        className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                        title={
                          alert.is_active ? "Disable alert" : "Enable alert"
                        }
                      >
                        {alert.is_active ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                          </svg>
                        )}
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => handleDeleteAlert(alert.id)}
                        className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete alert"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Alert Details */}
                  <div className="ml-10">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      {isAbove ? "Above" : "Below"} $
                      {parseFloat(alert.target_price).toFixed(2)}
                    </div>
                    <div className="flex items-center gap-2">
                      {isTriggered && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          Triggered {formatDate(alert.triggered_at)}
                        </span>
                      )}
                      {alert.source_type === "intel" && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                          from intel ›
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Footer */}
        {false && alerts.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-xs text-blue-800 dark:text-blue-300">
                Click any alert to view ticker details. Alerts update every 30
                seconds.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      <CreateAlertDialog
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        onAlertCreated={handleAlertCreated}
      />

      {/* Alert Source Modal */}
      {selectedAlert && (
        <AlertSourceModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
};

export default AlertsPanel;
